/**
 * Zustand auth store for the portal SPA.
 *
 * Manages authenticated user identity, role, tenant context,
 * feature flags, and white-label branding configuration.
 * Populated on successful Cognito authentication via Amplify v6.
 */

import { create } from 'zustand';

import type { AuthPayload, AuthState } from '../types/auth-state';

/**
 * Creates the initial (unauthenticated) state.
 */
function createInitialState(): Omit<AuthState, 'setAuth' | 'clearAuth' | 'switchRole'> {
  return {
    userId: null,
    displayName: null,
    tenantId: null,
    role: null,
    featureFlags: {},
    whitelabelConfig: null,
    isAuthenticated: false,
  };
}

/**
 * Zustand store for authentication state.
 *
 * Usage:
 * ```ts
 * const { userId, role, isAuthenticated } = useAuthStore();
 * const setAuth = useAuthStore((s) => s.setAuth);
 * ```
 */
export const useAuthStore = create<AuthState>((set) => ({
  ...createInitialState(),

  setAuth: (auth: AuthPayload) =>
    { set({
      userId: auth.userId,
      displayName: auth.displayName ?? null,
      tenantId: auth.tenantId,
      role: auth.role,
      featureFlags: auth.featureFlags,
      whitelabelConfig: auth.whitelabelConfig,
      isAuthenticated: true,
    }); },

  switchRole: (role) => { set({ role }); },

  clearAuth: () => { set(createInitialState()); },
}));
